/**
 * thread-jump.ts — DLITE-3849 Phase 6b
 *
 * Wire contract for the `commentary-thread-jump` CustomEvent that
 * commentary-ui dispatches when the user clicks an entity chip on an
 * expanded thread. Helios listens for the event on `document` (composed:
 * true crosses the Shadow DOM boundary) and uses the SINGLE `detail.target`
 * the user clicked to:
 *
 *   1. activate the panel that owns rows of that entity type; and
 *   2. focus that one row in the panel's grid.
 *
 * There is no filter mask and no banner — following a chip is a hyperlink to
 * one row, not a multi-row filter. `thread.snippet` and
 * `thread.authorDisplayName` are kept on the wire for parity with the
 * commentary-ui dispatcher (which still sends them), but helios does not
 * consume them today.
 *
 * `thread.linkedEntities` IS consumed, but only for its first `account` entry,
 * which helios takes as the account the thread is filed on
 * (`CommentaryFollowTarget.threadAccountId`). commentary-ui lists the primary
 * account first; if that ordering changes, the follow loads the wrong
 * account's session.
 *
 * MUST stay in lock-step with the commentary-ui counterpart at
 * `commentary-ui/src/features/comments-drawer/jump-events.ts`. Either side
 * changing the shape without the other will silently break the round-trip.
 */

/** Entity category in the wire payload. Must match commentary-ui exactly. */
export type CommentaryEntityType =
  | 'account'
  | 'transaction'
  | 'externalTransaction'
  | 'break'
  | 'asset'
  | 'suspense'

export interface CommentaryEntityRef {
  type: CommentaryEntityType
  /** Stringly-typed id. Numeric transactions arrive stringified. */
  id: string
  /**
   * Optional friendly label for the chip (DLITE-3911). A panel's entity source
   * may attach it (e.g. Smart Suspense → "Suspense <identifier> · Account <id>");
   * the raw `id` stays the stable jump/lookup key. commentary-ui renders it when
   * present, else falls back to `<Noun> <id>`. On a jump target, commentary-ui
   * fills it from the thread's stored description so a not-found notice can
   * name the entity the way its chip did.
   */
  label?: string
}

/**
 * What `follow$` emits: the clicked chip plus the account the thread it was
 * clicked in is filed on. That account differs from the drawer's own when the
 * drawer is showing an aggregate's rolled-up threads.
 */
export interface CommentaryFollowTarget extends CommentaryEntityRef {
  threadAccountId?: string
}

/** The `detail` payload of the `commentary-thread-jump` event. */
export interface ThreadJumpDetail {
  /** The chip the user clicked. The grid should select / highlight this row. */
  target: CommentaryEntityRef
  thread: {
    id: string
    /** Latest comment content. Sent by commentary-ui; not consumed by helios. */
    snippet: string
    /** Author display name. Sent by commentary-ui; not consumed by helios. */
    authorDisplayName: string
    /**
     * Primary + every secondary ref on the thread. The first `account` entry
     * must be the thread's own account — see the file header.
     */
    linkedEntities: CommentaryEntityRef[]
  }
}

/** The DOM event-name constant — single source of truth for the listener side. */
export const COMMENTARY_THREAD_JUMP_EVENT = 'commentary-thread-jump' as const

/** Convenience alias — what `document.addEventListener` sees. */
export type ThreadJumpEvent = CustomEvent<ThreadJumpDetail>
